import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';

import { progressBarLabel, toProgressBarState } from './progress-bar.util';

export type ProgressBarVariant = 'box' | 'line';

/**
 * Compact progress indicator.
 *
 * - `variant="box"` (default): small rounded box that fills from the left, e.g. in `dmb-status-matrix`
 * - `variant="line"`: thin full-width pill, e.g. in lists; usually combined with `showLabel`
 *
 * ```html
 * <dmb-progress-bar [value]="0.4" matTooltip="40%" />
 * <dmb-progress-bar [value]="0.4" variant="line" showLabel />
 * ```
 *
 * See {@link toProgressBarState} for the meaning of `value` and `relativeValue`.
 * Colours come from the theme tokens `--status-*`. The size can be changed with the CSS variables
 * `--dmb-progress-bar-width` and `--dmb-progress-bar-height`.
 */
@Component({
  selector: 'dmb-progress-bar',
  template: `
    @if (showLabel()) {
      <span class="label">{{ label() }}</span>
    }
    <div class="track">
      <div class="fill" [style.width.%]="state().fill * 100" [style.background-color]="state().color"></div>
    </div>
  `,
  styleUrl: './progress-bar.component.scss',
  host: {
    role: 'progressbar',
    'aria-valuemin': '0',
    'aria-valuemax': '100',
    '[attr.aria-valuenow]': 'ariaValue()',
    '[attr.data-state]': 'state().kind',
    '[attr.data-variant]': 'variant()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressBarComponent {
  /** Progress between 0 and 1, negative values mark an error */
  readonly value = input.required<number>();
  /** Optional relative value (0..2, 1 = normal) used to tint complete bars */
  readonly relativeValue = input<number | null | undefined>(undefined);
  readonly variant = input<ProgressBarVariant>('box');
  /** Shows the percentage above the bar */
  readonly showLabel = input(false, { transform: booleanAttribute });

  readonly state = computed(() => toProgressBarState(this.value(), this.relativeValue()));
  readonly label = computed(() => progressBarLabel(this.value()));
  readonly ariaValue = computed(() => (this.value() < 0 ? null : Math.floor(Math.min(this.value(), 1) * 100)));
}
